import { omit as _omit } from 'lodash-es';
import isEqual from 'react-fast-compare';

export type StoredDraft = {
  values: Record<string, unknown>;
  savedAt: number | null;
  // Excluded fields that held a value when the draft was saved — i.e. data the
  // user entered that will NOT come back on restore (e.g. an image upload).
  droppedFields: string[];
};

// ''/null/undefined are interchangeable for draft purposes: a field the user
// typed into and then emptied is the same as one never touched.
const isEmptyValue = (value: unknown): boolean =>
  value === '' || value === null || value === undefined;

const withoutEmptyValues = (values: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => !isEmptyValue(value)));

/**
 * Deep equality that ignores empty fields (''/null/undefined) at the top
 * level, so "typed a value then deleted it" compares equal to "never typed".
 */
export const valuesAreEffectivelyEqual = (a: unknown, b: unknown): boolean => {
  if (
    !a || !b || typeof a !== 'object' || typeof b !== 'object'
    || Array.isArray(a) || Array.isArray(b)
  ) {
    return isEqual(a, b);
  }
  return isEqual(
    withoutEmptyValues(a as Record<string, unknown>),
    withoutEmptyValues(b as Record<string, unknown>),
  );
};

// What FormikPersist should do with the stored draft on a given Formik update.
//   'clear' — drop the draft and cancel any pending save
//   'skip'  — leave storage untouched (no meaningful user edit)
//   'save'  — (re)persist the current values
export type PersistAction = 'clear' | 'skip' | 'save';

/**
 * Decide how a Formik update should affect the persisted draft. Kept pure (and
 * separate from the component) so the save/restore/discard rules can be tested
 * directly — this is where the phantom-draft, un-discardable-draft, and
 * timestamp-churn bugs lived.
 */
export const decidePersistAction = ({
  status,
  prevValues,
  nextValues,
  initialValues,
  restoredValues,
}: {
  status: unknown;
  prevValues: unknown;
  nextValues: unknown;
  initialValues: unknown;
  restoredValues?: unknown;
}): PersistAction => {
  // Submitted: drop the draft and stop persisting.
  if (status === 'submitted') return 'clear';

  // Only real value edits matter — ignore touched/errors/validation churn so a
  // focus/blur on an untouched field can't create a phantom draft.
  if (isEqual(prevValues, nextValues)) return 'skip';

  // Back to the (effectively) pristine form — Discard/reset, the comment
  // form's resetForm-on-submit, or the user emptying out what they typed.
  // Clear rather than keeping a draft that restores nothing meaningful.
  if (valuesAreEffectivelyEqual(nextValues, initialValues)) return 'clear';

  // The update caused by restoring a draft on mount — don't re-save it (that
  // would bump savedAt on every page load).
  if (restoredValues !== undefined && isEqual(nextValues, restoredValues)) return 'skip';

  return 'save';
};

export const serializeForStorage = (
  values: Record<string, unknown>,
  excludeFields?: string[]
): string => {
  const excluded = excludeFields ?? [];
  const filtered = excluded.length
    ? _omit(values, excluded)
    : values;
  const payload: StoredDraft = {
    values: filtered as Record<string, unknown>,
    savedAt: Date.now(),
    droppedFields: excluded.filter((field) => !isEmptyValue(values[field])),
  };
  return JSON.stringify(payload);
};

export const parseStoredDraft = (raw: string | null): StoredDraft | null => {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const obj = parsed as Record<string, unknown>;

  // New format: { values: {...}, savedAt: number, droppedFields: [...] }
  if ('values' in obj && obj.values && typeof obj.values === 'object') {
    const savedAt = typeof obj.savedAt === 'number' ? obj.savedAt : null;
    // Entries written before droppedFields existed parse as [] — meaning "no
    // user-entered data was dropped", so no re-attach warning for them.
    const droppedFields = Array.isArray(obj.droppedFields)
      ? (obj.droppedFields as unknown[]).filter((f): f is string => typeof f === 'string')
      : [];
    return { values: obj.values as Record<string, unknown>, savedAt, droppedFields };
  }

  // Legacy comment-form format: stored a Formik state slice like { values: {...} }
  // is already handled above. Fall through for truly unwrapped raw values is unlikely
  // given the prior code always wrote { values } shape, but guard anyway.
  return { values: obj, savedAt: null, droppedFields: [] };
};
